<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Widget from 'dashboard/modules/widget-preview/components/Widget.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { WIDGET_BUILDER_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import WidgetBrandKit from 'dashboard/components-next/WidgetBrandKit.vue';

export default {
  components: {
    Widget,
    NextButton,
    Editor,
    WidgetBrandKit,
  },
  props: {
    inbox: {
      type: Object,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      color: '#1f93ff',
      websiteName: '',
      welcomeHeading: '',
      welcomeTagline: '',
      replyTime: 'in_a_few_minutes',
      avatarFile: null,
      avatarUrl: '',
      launcherPreviewConfig: {
        style: 'standard',
        label: '',
        position: 'right',
        profileName: '',
        profileTagline: '',
      },
      previewViewport: 'mobile',
      welcomeTaglineEditorMenuOptions: WIDGET_BUILDER_EDITOR_MENU_OPTIONS,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    getReplyTimeOptions() {
      return [
        {
          key: 'in_a_few_minutes',
          value: 'in_a_few_minutes',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_FEW_MINUTES'
          ),
        },
        {
          key: 'in_a_few_hours',
          value: 'in_a_few_hours',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_FEW_HOURS'
          ),
        },
        {
          key: 'in_a_day',
          value: 'in_a_day',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_DAY'
          ),
        },
      ];
    },
    websiteNameValidationErrorMsg() {
      return this.v$.websiteName.$error
        ? this.$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.ERROR')
        : '';
    },
  },
  mounted() {
    this.setDefaults();
  },
  validations: {
    websiteName: { required },
  },
  methods: {
    setDefaults() {
      // Widget Settings
      const {
        name,
        welcome_title,
        welcome_tagline,
        widget_color,
        reply_time,
        avatar_url,
      } = this.inbox;
      this.websiteName = name;
      this.welcomeHeading = welcome_title;
      this.welcomeTagline = welcome_tagline;
      this.color = widget_color;
      this.replyTime = reply_time;
      this.avatarUrl = avatar_url;
      this.launcherPreviewConfig = {
        style:
          this.inbox.launcher_config?.style === 'profile_badge' ||
          this.inbox.launcher_config?.style === 'branded_pill'
            ? this.inbox.launcher_config.style
            : 'standard',
        label: this.inbox.launcher_config?.label || '',
        position: this.inbox.launcher_config?.position || 'right',
        profileName: this.inbox.launcher_config?.profile_name || name,
        profileTagline: this.inbox.launcher_config?.profile_tagline || '',
      };
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    handleLauncherPreviewChange(config) {
      this.launcherPreviewConfig = config;
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch('inboxes/deleteInboxAvatar', this.inbox.id);
        this.avatarFile = null;
        this.avatarUrl = '';
        useAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.DELETE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.DELETE.API.ERROR_MESSAGE'
              )
        );
      }
    },
    async updateWidget() {
      try {
        const channel = {
          widget_color: this.color,
          welcome_title: this.welcomeHeading,
          welcome_tagline: this.welcomeTagline,
          reply_time: this.replyTime,
        };

        if (this.launcherPreviewConfig.style !== 'standard') {
          channel.launcher_config = {
            style: this.launcherPreviewConfig.style,
            label: this.launcherPreviewConfig.label,
            position: this.launcherPreviewConfig.position,
            profile_name: this.launcherPreviewConfig.profileName,
            profile_tagline: this.launcherPreviewConfig.profileTagline,
          };
        }

        const payload = {
          id: this.inbox.id,
          name: this.websiteName,
          channel,
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          error.message ||
            this.$t(
              'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.ERROR_MESSAGE'
            )
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-8 grid gap-10 lg:grid-cols-[minmax(0,1fr)_26rem]">
    <div>
      <WidgetBrandKit
        v-model:widget-color="color"
        :inbox="inbox"
        @preview-change="handleLauncherPreviewChange"
      />
      <section
        class="mb-8 max-w-3xl border-t border-n-slate-6 py-8 dark:border-n-slate-7"
      >
        <h2 class="mb-1 text-lg font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.CONTENT.TITLE') }}
        </h2>
        <p class="mb-6 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.CONTENT.DESCRIPTION') }}
        </p>
        <form @submit.prevent="updateWidget">
          <woot-avatar-uploader
            :label="$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.LABEL')"
            :src="avatarUrl"
            delete-avatar
            @on-avatar-select="handleImageUpload"
            @on-avatar-delete="handleAvatarDelete"
          />
          <woot-input
            v-model="websiteName"
            :class="{ error: v$.websiteName.$error }"
            :label="
              $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.LABEL')
            "
            :placeholder="
              $t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.PLACE_HOLDER'
              )
            "
            :error="websiteNameValidationErrorMsg"
            @blur="v$.websiteName.$touch"
          />
          <woot-input
            v-model="welcomeHeading"
            :label="
              $t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_HEADING.LABEL'
              )
            "
            :placeholder="
              $t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_HEADING.PLACE_HOLDER'
              )
            "
          />
          <Editor
            v-model="welcomeTagline"
            :label="
              $t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_TAGLINE.LABEL'
              )
            "
            :placeholder="
              $t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_TAGLINE.PLACE_HOLDER'
              )
            "
            :max-length="255"
            :enabled-menu-options="welcomeTaglineEditorMenuOptions"
            class="mb-4"
          />
          <label class="block text-sm font-medium text-n-slate-12">
            <span class="mb-2 block">{{
              $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.LABEL')
            }}</span>
            <select v-model="replyTime">
              <option
                v-for="option in getReplyTimeOptions"
                :key="option.key"
                :value="option.value"
              >
                {{ option.text }}
              </option>
            </select>
          </label>
          <div
            class="mt-8 border-t border-n-slate-6 pt-6 dark:border-n-slate-7"
          >
            <h3 class="mb-1 text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.INSTALLATIONS_LABEL') }}
            </h3>
            <p class="mb-4 text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.INSTALLATIONS_DESCRIPTION') }}
            </p>
            <div class="space-y-2">
              <a
                class="flex items-center justify-between rounded-lg border border-n-slate-6 px-4 py-3 text-sm hover:bg-n-slate-3 dark:border-n-slate-7 dark:hover:bg-n-solid-3"
                href="/downloads/1cs-live-chat.zip"
                download
              >
                <span class="font-medium text-n-slate-12">{{
                  $t('INBOX_MGMT.WIDGET_BRAND_KIT.WORDPRESS.TITLE')
                }}</span>
                <span class="text-n-brand">{{
                  $t('INBOX_MGMT.WIDGET_BRAND_KIT.WORDPRESS.DOWNLOAD')
                }}</span>
              </a>
              <a
                class="flex items-center justify-between rounded-lg border border-n-slate-6 px-4 py-3 text-sm hover:bg-n-slate-3 dark:border-n-slate-7 dark:hover:bg-n-solid-3"
                href="/downloads/onecs-live-chat.tpl"
                download
              >
                <span class="font-medium text-n-slate-12">{{
                  $t('INBOX_MGMT.WIDGET_BRAND_KIT.GTM.TITLE')
                }}</span>
                <span class="text-n-brand">{{
                  $t('INBOX_MGMT.WIDGET_BRAND_KIT.GTM.DOWNLOAD')
                }}</span>
              </a>
              <details
                class="rounded-lg border border-n-slate-6 px-4 py-3 dark:border-n-slate-7"
              >
                <summary
                  class="cursor-pointer text-sm font-medium text-n-slate-12"
                >
                  {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.EMBED.TITLE') }}
                </summary>
                <woot-code class="mt-3" :script="inbox.web_widget_script" />
              </details>
            </div>
          </div>
          <NextButton
            type="submit"
            class="mt-6"
            :label="
              $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.BUTTON_TEXT')
            "
            :is-loading="uiFlags.isUpdating"
            :disabled="v$.$invalid || uiFlags.isUpdating"
          />
        </form>
      </section>
    </div>

    <aside class="lg:sticky lg:top-6 lg:self-start">
      <div class="mb-3 flex items-center justify-between gap-3">
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.PREVIEW.TITLE') }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.PREVIEW.DESCRIPTION') }}
          </p>
        </div>
        <div class="inline-flex rounded-lg bg-n-slate-3 p-1 dark:bg-n-solid-3">
          <button
            type="button"
            class="rounded-md px-3 py-1.5 text-xs font-medium"
            :class="
              previewViewport === 'mobile'
                ? 'bg-n-alpha-1 text-n-slate-12 shadow-sm dark:bg-n-solid-1'
                : 'text-n-slate-11'
            "
            @click="previewViewport = 'mobile'"
          >
            {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.PREVIEW.MOBILE') }}
          </button>
          <button
            type="button"
            class="rounded-md px-3 py-1.5 text-xs font-medium"
            :class="
              previewViewport === 'desktop'
                ? 'bg-n-alpha-1 text-n-slate-12 shadow-sm dark:bg-n-solid-1'
                : 'text-n-slate-11'
            "
            @click="previewViewport = 'desktop'"
          >
            {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.PREVIEW.DESKTOP') }}
          </button>
        </div>
      </div>
      <div
        class="relative flex items-end justify-center overflow-hidden border-8 border-n-slate-12 bg-n-slate-3 p-4 shadow-xl dark:bg-n-solid-3"
        :class="
          previewViewport === 'mobile'
            ? 'min-h-[38rem] rounded-[2rem]'
            : 'min-h-[31rem] rounded-xl'
        "
      >
        <div
          v-if="previewViewport === 'mobile'"
          class="absolute self-start rounded-full bg-n-slate-12 px-16 py-3"
        />
        <div v-else class="absolute left-4 top-4 flex gap-1.5">
          <span class="h-2.5 w-2.5 rounded-full bg-n-slate-7" />
          <span class="h-2.5 w-2.5 rounded-full bg-n-slate-7" />
          <span class="h-2.5 w-2.5 rounded-full bg-n-slate-7" />
        </div>
        <Widget
          :welcome-heading="welcomeHeading"
          :welcome-tagline="welcomeTagline"
          :website-name="websiteName"
          :logo="avatarUrl"
          is-online
          :reply-time="replyTime"
          :color="color"
          :widget-bubble-position="launcherPreviewConfig.position"
          :launcher-style="launcherPreviewConfig.style"
          :launcher-label="launcherPreviewConfig.label"
          :launcher-profile-name="launcherPreviewConfig.profileName"
          :launcher-profile-tagline="launcherPreviewConfig.profileTagline"
          :preview-viewport="previewViewport"
        />
      </div>
      <p class="mt-3 text-center text-xs text-n-slate-11">
        {{ $t('INBOX_MGMT.WIDGET_BRAND_KIT.PREVIEW.TAP_HINT') }}
      </p>
    </aside>
  </div>
</template>
